import { Box, Icon, Typography } from "@outschool/backpack";
import { REFER_CREDIT_CENTS, centsToDollars } from "@outschool/business-rules";
import { ParentReferralStatus } from "@outschool/gql-backend-generated";
import { faCheck, faChevronRight } from "@outschool/icons";
import {
  TFunction,
  Trans,
  renderCurrencyString,
  useTranslation
} from "@outschool/localization";
import { useQueryWithPreviousData } from "@outschool/ui-apollo";
import { Modal, ModalProps } from "@outschool/ui-legacy-component-library";
import React from "react";

import * as Env from "../../shared/Env";
import {
  ReferralsQuery,
  ReferralsQueryVariables
} from "../../shared/generated/graphql";
import * as Routes from "../../shared/Routes";
import ErrorMessage from "./ErrorMessage";
import Link from "./Link";
import { ReferredParentsList, UserReferrals } from "./Referrals";

const ReferralsModal = (
  props: Omit<ModalProps, "children" | "sx"> & { isEducator: boolean }
) => {
  const { t } = useTranslation("client\\components\\ReferralsModal");
  const { isEducator, ...modalProps } = props;

  return (
    <Modal
      sx={{
        display: "flex",
        flexFlow: "column",
        alignItems: "center",
        textAlign: "center"
      }}
      {...modalProps}
    >
      <Typography
        variant="h4"
        sx={{
          marginBottom: "0.5em"
        }}
      >
        {t`Referral Status`}
      </Typography>
      <Box
        component="p"
        sx={{
          marginBottom: "2em"
        }}
      >
        <Trans t={t}>
          To receive credits, your friend needs to{" "}
          <strong>sign up and attend their first class</strong>.
        </Trans>
        {isEducator && (
          <>
            <br />
            <br />
            {t(
              "Referrals using your unique OUTreach code do not appear in the list below."
            )}
          </>
        )}
      </Box>
      <ReferralsModalListing t={t} />
    </Modal>
  );
};
export default ReferralsModal;

const ReferralsModalListing = ({ t }: { t: TFunction }) => {
  const { loading, data, error } = useQueryWithPreviousData<
    ReferralsQuery,
    ReferralsQueryVariables
  >(UserReferrals);

  if (loading) {
    return null;
  } else if (error) {
    return (
      <ErrorMessage value={error} showStatusPageLink={Env.IS_READ_ONLY_MODE} />
    );
  } else {
    return (
      <>
        <div>{t`My total earnings`}</div>
        <Box
          sx={{
            fontWeight: "fontWeightBold",
            fontSize: "2em"
          }}
        >
          {"$"}
          {centsToDollars(data?.currentUser?.totalReferralEarnings ?? 0)}
        </Box>
        <Box
          sx={{
            fontSize: "0.875em",
            marginBottom: "2em"
          }}
        >
          <Link to={Routes.transactionsPath()}>
            {t`See my transactions`} <Icon icon={faChevronRight} />
          </Link>
        </Box>
        <Box
          sx={{
            overflowY: "auto",
            width: "100%",
            maxHeight: "300px"
          }}
        >
          <ReferredParentsList
            referredParents={data?.currentUser?.referredParents ?? []}
            t={t}
            omitTitle={true}
            sx={{
              width: "100%",
              thead: { backgroundColor: "white", position: "sticky", top: 0 }
            }}
            makeLabel={makeReferralLabel}
          />
        </Box>
      </>
    );
  }
};

const makeReferralLabel = (status: ParentReferralStatus, t: TFunction) => {
  switch (status) {
    case ParentReferralStatus.SignedUp:
      return <>{t`Hasn’t taken a class yet`}</>;
    case ParentReferralStatus.CreditEarned:
    case ParentReferralStatus.CreditClaimed:
      return (
        <>
          {t("{{creditString}} earned!", {
            creditString: renderCurrencyString({
              priceInCents: REFER_CREDIT_CENTS,
              showCurrencyCode: false
            })
          })}{" "}
          <Icon icon={faCheck} color="primary" />
        </>
      );
  }
};
